import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './Alert.module.css';
import { Modal } from 'react-bootstrap';
import { getAlertColorsAndIcon, formatCompletedDate, formatAlertDescription } from '../../utils/alertUtils';
import { formatDateString } from '../../utils/dateUtils';
import useAxios from '../../services/AxiosConfig';
import { useDispatch } from 'react-redux';
import { showAlert } from '../PerinHealthAlertSlice';

const AlarmOptionsModal = ({ optionsModal, onClose, handleAlertAction }) => {

    const dispatch = useDispatch();

    const { id, unitType, fullName, dateFormat } = useSelector((state) => state.userInfo);
    const patient = useSelector((state) => state.patient);
    const [newNote, setNewNote] = useState('');
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const getDeviceName = (deviceId) => {
        const foundDevice = patient.carePrograms
            .flatMap(program => program.devices)
            .find(device => device.deviceId === deviceId);
        return foundDevice?.deviceName ?? "Unknown device";
    };

    let { icon } = getAlertColorsAndIcon(optionsModal.alert);

    const submitNote = async () => {
        try {
            const patientNotes = await fetchPatientNotes();
            const noteDate = formatDateString(new Date().toISOString(), unitType);
            const noteWithHeader = `**${fullName} - ${noteDate}**\n${newNote}`;

            const existingAlarmNote = patientNotes.find((item) => item.alertId === optionsModal.alert.alertId);
            if (existingAlarmNote) {
                // update existing note
                const updatedNote = `${existingAlarmNote.notes}\n\n${noteWithHeader}`;
                const updatingFile = new Blob([updatedNote], { type: 'text/plain' });
                const updatingFormData = new FormData();
                updatingFormData.append('isDraft', false);
                updatingFormData.append('noteFile', updatingFile, `notes.txt`);
                try {
                    await httpService.put(`/patient/notes/update/${existingAlarmNote.noteId}`, updatingFormData)
                        .then(response => {
                            return response;
                        })
                        .catch(err => console.error('Failed to add note:', err));
                } catch (error) {
                    console.error('Error adding note:', error);
                    throw error;
                }
            } else {

                // create new note
                const newNoteText = `${optionsModal.alert.alert_name} - ${optionsModal.alert.alert_description}\n\n${noteWithHeader}`;

                const file = new Blob([newNoteText], { type: 'text/plain' });

                const formData = new FormData();
                formData.append('patientId', patient.id);
                formData.append('physicianId', id);
                formData.append('noteType', "Alarm");
                formData.append('isDraft', false);
                formData.append('alertId', optionsModal.alert.alertId)

                const currentDate = new Date();
                const formattedDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
                const formattedTime = `${currentDate.getHours().toString().padStart(2, '0')}-${currentDate.getMinutes().toString().padStart(2, '0')}-${currentDate.getSeconds().toString().padStart(2, '0')}`;

                formData.append('noteFile', file, `${formattedDate}_${formattedTime}_notes.txt`);
                await httpService.post(`patient/notes`, formData)
                    .then(response => {
                        return response;
                    })
                    .catch(err => console.error('Failed to add note:', err));
            }
            dispatch(
                showAlert({
                    header: "Success",
                    message: `Note created successfully`,
                    type: 'success',
                })
            );
            onClose();
            setNewNote('');
        } catch (error) {
            console.error('Error adding note:', error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: `Error creating note`,
                    type: 'danger'
                })
            );

        }
    };

    const fetchPatientNotes = async () => {
        try {
            const response = await httpService.get(`patient/${patient.id}/notes`);
            const fetchPromises = response.data.data.map(async (note) => {
                try {
                    // Fetch the note content
                    let textContent = "Not available";
                    const responseData = await fetch(note.noteUrl);
                    const text = await responseData.text();
                    textContent = text;

                    let doctorName = note.physicianId;
                    // Fetch the doctor's name using the physicianId
                    await httpService.get(`user/${note.physicianId}`).then((responseData) => {
                        doctorName = `${responseData.data.data.firstName} ${responseData.data.data.lastName}`;
                    })
                        .catch((err) => {
                            console.log(err);
                        });
                    // Return a new object with only the desired properties
                    return {
                        patientId: note.patientId,
                        noteId: note.noteId,
                        title: note.noteUrl.split("/").slice(-2, -1)[0],  // Extract the note type from the URL
                        doctor: doctorName,
                        updatedAt: note.updatedAt,
                        submissionDate: note.updatedAt,
                        notes: textContent,
                        isDraft: note.isDraft,
                        attachmentUrls: note.attachmentUrls,
                        alertId: note.alertId ?? null
                    };
                } catch (error) {
                    console.log(`Failed to fetch content for note ID ${note.noteId}: ${error}`);
                    return null; // Ignore this note if there's an error
                }
            });

            const notesWithContent = (await Promise.all(fetchPromises)).filter(note => note !== null);
            return notesWithContent;

        } catch (error) {
            console.error('Error fetching patient notes:', error);
            return [];
        }
    };

    return (
        <Modal
            show={optionsModal.open}
            onHide={onClose}
            centered
        >
            <Modal.Header closeButton>
                <div className="subtitle-1">
                    Alarm Options
                </div>
            </Modal.Header>
            <Modal.Body>
                <div styles={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ display: "flex" }}>
                        {icon}
                        <span className="subtitle-1" style={{ padding: "0 5px" }}>
                            {optionsModal.alert.alert_display_name ?? optionsModal.alert.alert_name}
                        </span>
                        {formatCompletedDate(optionsModal.alert, dateFormat)}
                    </div>
                    {`${String(optionsModal.alert.alert_priority).charAt(0).toUpperCase() + String(optionsModal.alert.alert_priority).slice(1)} Priority: ${formatAlertDescription(optionsModal.alert, unitType)}`}

                    <hr></hr>
                    {optionsModal.alert.alert_modalityId &&
                        <div className={styles.alarmOptionsItem}>
                            <div>
                                <span>View on Graph</span>
                            </div>
                            <button onClick={() => handleAlertAction(optionsModal.alert, 'viewOnGraph')} className={styles.alarmOptionsButton}>
                                View
                            </button>
                        </div>

                    }
                    <div className={styles.alarmOptionsItem}>
                        <div>
                            <span>Escalate to a Team Member</span>
                        </div>
                        <button onClick={() => handleAlertAction(optionsModal.alert, 'escalate')} className={styles.alarmOptionsButton}>
                            Escalate
                        </button>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", }}>
                        <span>Write Documentation</span>
                        <span className="body-2" style={{ color: "#686869" }}>Any notes written regarding this alarm will saved in the “Notes” section of your patients’ profile.</span>
                    </div>

                    <textarea
                        className={styles.newNote}
                        placeholder='Add Notes'
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                    >
                    </textarea>
                    <button
                        style={{ marginLeft: 'auto', marginTop: '10px' }}
                        disabled={newNote === ''}
                        onClick={submitNote}
                        className={styles.alarmOptionsButton}
                    >
                        Submit
                    </button>
                </div>
            </Modal.Body>

        </Modal>
    );
};

export default AlarmOptionsModal;
